import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { preloadContentOwnershipCollaborators } from '@confluence/content-ownership/entry-points/preloadContentOwnershipCollaborators.ts';
import { expVal, expValEquals, isExperimentEnabled } from '@confluence/feature-experiments';
import { preloadReadTime } from '@confluence/read-time/entry-points/preloadReadTime.ts';
import { preloadPageReactions } from '@confluence/reactions/entry-points/preloadPageReactions.ts';
import { preloadClassificationContextForViewContent } from '@confluence/data-classification/entry-points/queries.ts';
import { preloadContentAnalyticsViewers } from '@confluence/confluence-analytics/entry-points/preloadContentAnalyticsViewers.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import ContentHeaderUnifiedQuery from '../__generated__/ContentHeaderUnifiedQuery.graphql.ts';
import { DEFAULT_CONTRIBUTORS_FETCH_LIMIT } from '../defaultQueryVariables.js';

export type preloadEditorByLineDataProps = {
	isUnpublishedDraft?: boolean;
	spaceKey?: string;
	contentId: string;
};

export function preloadEditorByLineData({
	isUnpublishedDraft,
	spaceKey,
	contentId,
}: preloadEditorByLineDataProps): Promise<void | any[] | Error[]> {
	const queries: Promise<any>[] = [
		query({
			query: ContentHeaderUnifiedQuery,
			variables: {
				contentId,
				spaceKey,
				versionOverride: null,
				embeddedContentRender: null,
				limit: DEFAULT_CONTRIBUTORS_FETCH_LIMIT,
				isMarkdownModeEnabled: expValEquals('cc-markdown-mode', 'isEnabled', true),
				...(expValEquals('confluence_classification_level_v2', 'isEnabled', true)
					? { useClassificationLevelV2: true }
					: {}),
				includeDevDocsBylineConnection:
					isExperimentEnabled('confluence_1p_and_3p_connection_byline_experiment') ||
					isExperimentEnabled('confluence_dev_docs'),
				useObjectHeaderFragment:
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_obj_header_frag_dedup') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false),
				includeClassification:
					!StatsigConfigurations.isKillSwitchOn(
						'confluence_vp_classification_context_frag_reader',
					) && expVal('confluence_exp_vp_document', 'isEnabled', false),
				...(isUnpublishedDraft && { status: ['draft'] }),
			},
		}),
		preloadPageReactions({
			pageId: contentId,
			containerId: spaceKey,
		}),
	];

	if (contentId && spaceKey && expValEquals('dual_agent_avatar_card', 'isEnabled', true)) {
		const { cloudId } = getPreloaderFnContext();
		if (cloudId) {
			queries.push(preloadContentOwnershipCollaborators({ cloudId, contentId, spaceKey }));
		}
	}

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		queries.push(
			preloadReadTime({ contentId, useCache: false, status: ['current', 'draft'] }),
			preloadContentAnalyticsViewers(contentId),
			preloadClassificationContextForViewContent(contentId),
		);
	}

	return waitForAllReturnWhenError(queries);
}
